<template>
  <BittsQuickFilters
    class="sharing-settings-by-partners-actions"
    :filter-options="filterOptions"
    :filter-value-options="filterValueOptions"
    @update-filters="handleUpdateFilters"
  >
    <template #prefix-controls>
      <slot name="segment" />
    </template>
    <!-- Renders nothing for the other filters, which then fall back to their
         own option lists. -->
    <template #content="{ filter }">
      <div
        v-if="isPopulationFilter(filter)"
        class="sharing-settings-by-partners-actions__placeholder"
      >
        <div class="font-bold text-neutral-text-strong">Coming soon</div>
        <div>Filtering by populations is not available yet.</div>
      </div>
    </template>
    <template #controls>
      <div class="flex items-center gap-6">
        <BittsInput
          v-model="searchValue"
          type="search"
          name="partner-search"
          placeholder="Search partners"
          size="small"
          @update:model-value="debouncedSearch"
        />
        <slot name="actions" />
      </div>
    </template>
  </BittsQuickFilters>
</template>

<script setup lang="ts">
import { BittsInput, BittsQuickFilters } from '@crossbeam/bitts';
import type { FilterProp } from '@crossbeam/bitts/src/BittsQuickFilter/types';

import { useDebounceFn } from '@vueuse/core';
import { computed, ref } from 'vue';

import {
  OWNER_FILTER,
  PARTNER_FILTER,
  type PartnerTabFilterValues,
  TAGS_FILTER,
} from '../composables/usePartnerTabFilters';

const { partnerOptions, ownerOptions, tagOptions } = defineProps<{
  partnerOptions: FilterProp[];
  ownerOptions: FilterProp[];
  tagOptions: FilterProp[];
}>();
const emit = defineEmits<{
  'search-applied': [value: string];
  'filters-applied': [filterValues: PartnerTabFilterValues];
}>();
/**
 * The design calls for five categories, so both population filters are listed
 * here and open on a placeholder panel until they are built.
 */
const PARTNER_POPULATIONS_FILTER = 'partner_populations';
const MY_POPULATIONS_FILTER = 'my_populations';

const filterOptions: FilterProp[] = [
  {
    label: 'Partner Populations',
    value: PARTNER_POPULATIONS_FILTER,
    faIcon: ['fak', 'population'],
    datatype: 'text',
    noCustomValue: true,
  },
  {
    label: 'My Populations',
    value: MY_POPULATIONS_FILTER,
    faIcon: ['fak', 'population'],
    datatype: 'text',
    noCustomValue: true,
  },
  {
    label: 'Partner',
    value: PARTNER_FILTER,
    faIcon: ['fak', 'partner'],
    datatype: 'text',
    useSearch: true,
    noCustomValue: true,
  },
  {
    label: 'Owner',
    value: OWNER_FILTER,
    faIcon: ['far', 'user'],
    datatype: 'text',
    noCustomValue: true,
  },
  {
    label: 'Tags',
    value: TAGS_FILTER,
    faIcon: ['far', 'tag'],
    datatype: 'text',
    noCustomValue: true,
  },
];

const POPULATION_FILTERS = [PARTNER_POPULATIONS_FILTER, MY_POPULATIONS_FILTER];

function isPopulationFilter(filter: FilterProp) {
  return POPULATION_FILTERS.includes(String(filter.value));
}

const filterValueOptions = computed<Record<string, FilterProp[]>>(() => ({
  [PARTNER_POPULATIONS_FILTER]: [],
  [MY_POPULATIONS_FILTER]: [],
  [PARTNER_FILTER]: partnerOptions,
  [OWNER_FILTER]: ownerOptions,
  [TAGS_FILTER]: tagOptions,
}));

const searchValue = ref('');

const debouncedSearch = useDebounceFn((value: string) => {
  emit('search-applied', value);
}, 300);

/**
 * BittsQuickFilters hands back every datatype it supports; the simple filters
 * here only ever carry arrays, so anything else is treated as unset.
 */
function handleUpdateFilters({
  filterValues,
}: {
  filterValues: Record<
    string,
    (string | number)[] | Record<string, unknown> | null
  >;
}) {
  const applied: PartnerTabFilterValues = {};
  Object.entries(filterValues).forEach(([key, value]) => {
    if (Array.isArray(value)) applied[key] = value;
  });
  emit('filters-applied', applied);
}
</script>

<style scoped lang="pcss">
@reference "../../../../css/theme.css";
.sharing-settings-by-partners-actions__placeholder {
  @apply flex flex-col gap-4 p-16 text-center text-neutral-text;
}
</style>
